import { useState } from "react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { newsItems } from "@/mocks/newsData";
import SocialShare from "@/components/feature/SocialShare";

const categoryColors: Record<string, string> = {
  publication: "bg-tie-teal/10 text-tie-teal",
  conference: "bg-tie-navy/10 text-tie-navy",
  announcement: "bg-amber-50 text-amber-700",
  milestone: "bg-emerald-50 text-emerald-700",
};

function getYouTubeVideoId(url: string): string | null {
  const match = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

interface NewsCardProps {
  item: (typeof newsItems)[0];
  index: number;
}

function VideoCard({ item, index }: NewsCardProps) {
  const [playing, setPlaying] = useState(false);
  const videoId = getYouTubeVideoId(item.externalUrl);
  const thumbUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : undefined;

  return (
    <ScrollReveal delay={index * 120}>
      <div className="group h-full bg-tie-grey-light rounded-2xl overflow-hidden border-2 border-transparent hover:border-red-400 transition-all duration-300 flex flex-col">
        {/* Video / Thumbnail Area */}
        <div className="relative h-44 md:h-52 overflow-hidden shrink-0 bg-tie-navy">
          {playing && videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={item.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <img
                src={thumbUrl}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-tie-navy/30 group-hover:bg-tie-navy/20 transition-colors" />
              {/* Play Button */}
              <button
                onClick={() => setPlaying(true)}
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                aria-label={`Play ${item.title}`}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-red-500/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1" />
                </div>
              </button>
            </>
          )}
          {/* Channel Badge */}
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500 text-white text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              {item.source}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6 flex flex-col flex-1">
          <div className="flex items-center gap-3 text-xs text-tie-text-muted mb-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line" />
              {item.date}
            </span>
            <span className="inline-flex items-center gap-1 text-red-500 font-medium">
              <i className="ri-movie-line" />
              Video
            </span>
          </div>
          <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2 leading-snug group-hover:text-red-500 transition-colors">
            {item.title}
          </h3>
          <p className="text-sm text-tie-text-secondary leading-relaxed line-clamp-3 mb-4">
            {item.excerpt}
          </p>
          <div className="mt-auto pt-4 border-t border-tie-grey-dark flex items-center justify-between flex-wrap gap-3">
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-red-500 hover:text-red-600 transition-colors"
            >
              Watch on YouTube
              <i className="ri-external-link-line ml-1.5 text-xs" />
            </a>
            <SocialShare
              url={item.externalUrl}
              title={item.title}
              excerpt={item.excerpt}
              compact
            />
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}

function ArticleCard({ item, index }: NewsCardProps) {
  return (
    <ScrollReveal delay={index * 120}>
      <div className="group h-full bg-tie-grey-light rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300 flex flex-col">
        {/* Image Area */}
        <a
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative h-44 md:h-52 overflow-hidden shrink-0"
        >
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/40 to-transparent" />
          <div className="absolute top-3 left-3">
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${categoryColors[item.category]}`}
            >
              <i className="ri-global-line text-[10px]" />
              {item.source}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm">
              <i className="ri-external-link-line text-[10px] text-white" />
            </span>
          </div>
        </a>

        {/* Content */}
        <div className="p-5 md:p-6 flex flex-col flex-1">
          <div className="flex items-center gap-3 text-xs text-tie-text-muted mb-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line" />
              {item.date}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" />
              {item.readTime}
            </span>
          </div>
          <a
            href={item.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2 leading-snug group-hover:text-tie-teal transition-colors">
              {item.title}
            </h3>
          </a>
          <p className="text-sm text-tie-text-secondary leading-relaxed line-clamp-3 mb-4">
            {item.excerpt}
          </p>
          <div className="mt-auto pt-4 border-t border-tie-grey-dark flex items-center justify-between flex-wrap gap-3">
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
            >
              via {item.source}
              <i className="ri-external-link-line ml-1.5 text-xs opacity-60" />
            </a>
            <SocialShare
              url={item.externalUrl}
              title={item.title}
              excerpt={item.excerpt}
              compact
            />
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}

export default function NewsSection() {
  // Prioritize video items first, then fill with image articles
  const videoItems = newsItems.filter(
    (n) =>
      n.externalUrl?.includes("youtube.com") && n.readTime === "Video"
  );
  const imageArticles = newsItems.filter(
    (n) => n.image && !n.externalUrl?.includes("youtube.com")
  );

  const latest = [...videoItems, ...imageArticles].slice(0, 3);

  return (
    <section className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 md:mb-14">
            <ScrollReveal>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                  <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                    / Media Coverage
                  </span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                  TIE in the News
                </h2>
                <p className="mt-3 md:mt-4 text-base text-tie-text-secondary max-w-xl">
                  Live coverage, press highlights, and programme milestones from Textile Today and partner media outlets.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <Link
                to="/media"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-tie-navy text-white text-sm font-medium hover:bg-tie-navy/90 transition-all duration-300 whitespace-nowrap shrink-0"
              >
                View All Updates
                <i className="ri-arrow-right-line" />
              </Link>
            </ScrollReveal>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {latest.map((item, index) =>
              item.externalUrl?.includes("youtube.com") &&
              item.readTime === "Video" ? (
                <VideoCard key={item.id} item={item} index={index} />
              ) : (
                <ArticleCard key={item.id} item={item} index={index} />
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}